import React, { useEffect, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { FaCar } from 'react-icons/fa';
import { HiOutlineRectangleGroup } from 'react-icons/hi2';
import { useDispatch } from 'react-redux';
import { vehicleGroupService } from '../../Services/vehicle.group.service';
import { vehicleService } from '../../Services/vehicle.service';
import { useSelected } from '../../contexts/SelectedContext';
import { Filters } from '../../components/Filters';
import SyncFusionDateTimePicker from '../Components/SyncFusionDateTimePicker/SyncFusionDateTimePicker';
import { TbFileTypePdf, TbFileTypeXls } from 'react-icons/tb';
import { reportService } from '../../Services/report.service';
import { downloadService } from '../../Services/download.service';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page } from '@syncfusion/ej2-react-grids';
import Spinner from '../Components/Spinner';
import { ToastComponent } from '../Components/ToastComponent';

export const MaintenanceCompleted = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch(); /** eslint-disable-next-line */
    dispatch({ type: "title", value: t("MAINTENANCES.COMPLETED.TITLE") });

    const { selectedValues } = useSelected();
    const selectedGroup = selectedValues.veiculos?.[0] || 0;
    const selectedVehicle = selectedValues.veiculosGroups?.[0] || 0;

    const [vehicleGroups, setVehicleGroups] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [filtersLoading, setFiltersLoading] = useState(false);
    const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [toastParams, setToastParams] = useState(JSON.stringify({ show: false }));
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [pageSize, setPageSize] = useState(10);

    const calculatePageSize = () => {
        const available = window.innerHeight - 320;
        setPageSize(Math.max(5, Math.floor(available / 30)));
    };

    useLayoutEffect(() => {
        calculatePageSize();
    }, []);

    useEffect(() => {
        let timeoutId;
        const handleResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(calculatePageSize, 300);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            clearTimeout(timeoutId);
        };
    }, []);

    const buildFilters = () => {
        const filters = { Completed: "S" };
        if (selectedGroup && selectedGroup !== "TODOS" && selectedGroup !== 0) filters.GroupId = selectedGroup;
        if (selectedVehicle && selectedVehicle !== "TODOS" && selectedVehicle !== 0) filters.VehicleId = selectedVehicle;
        if (startDate) filters.StartDate = new Date(startDate).toISOString().split("T")[0];
        if (endDate) filters.EndDate = new Date(endDate).toISOString().split("T")[0];
        return filters;
    };

    const fetchVehicleGroups = async () => {
        try {
            setFiltersLoading(true);
            const groups = await vehicleGroupService.fetchGroups();
            if (Array.isArray(groups)) {
                setVehicleGroups(groups);
                await fetchVehiclesByGroup(groups.length === 1 ? groups[0].vehicleGroupId : 0);
            } else {
                setVehicleGroups([]);
                await fetchVehiclesByGroup(0);
            }
        } catch (error) {
            console.log(error);
            setVehicleGroups([]);
        } finally {
            setFiltersLoading(false);
        }
    };

    const fetchVehiclesByGroup = async (groupId) => {
        setIsLoadingVehicles(true);
        try {
            const response = await vehicleService.fetchVehicles(groupId);
            setVehicles(Array.isArray(response) ? response : []);
        } catch (error) {
            console.log(error);
            setVehicles([]);
        } finally {
            setIsLoadingVehicles(false);
        }
    };

    const handleSearch = async () => {
        setIsLoading(true);
        try {
            const response = await reportService.fetchSummaryMaintenances(buildFilters());
            setTableData(Array.isArray(response) ? response : []);
        } catch (error) {
            console.log(error);
            setTableData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadXls = async () => {
        setIsLoading(true);
        try {
            const response = await reportService.fetchSummaryMaintenancesXls(buildFilters());
            if (response) downloadService.downloadApiFile(response, "xls", "manutencoes-concluidas");
            else setToastParams(JSON.stringify({ show: true, title: "Erro", body: "Não foi possível gerar o arquivo.", color: "danger" }));
        } catch (error) {
            setToastParams(JSON.stringify({ show: true, title: "Erro", body: error?.response?.data?.message || "Erro ao gerar XLS.", color: "danger" }));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadPdf = async () => {
        setIsLoading(true);
        try {
            const response = await reportService.fetchSummaryMaintenancesPdf(buildFilters());
            if (response) downloadService.downloadApiFile(response, "pdf", "manutencoes-concluidas");
            else setToastParams(JSON.stringify({ show: true, title: "Erro", body: "Não foi possível gerar o arquivo.", color: "danger" }));
        } catch (error) {
            setToastParams(JSON.stringify({ show: true, title: "Erro", body: error?.response?.data?.message || "Erro ao gerar PDF.", color: "danger" }));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (selectedGroup !== null && selectedGroup !== undefined) {
            fetchVehiclesByGroup(selectedGroup);
        }
    }, [selectedGroup]);

    useEffect(() => {
        fetchVehicleGroups();
    }, []);

    return (
        <div className='ml-4'>
            <ToastComponent params={toastParams} closeToast={() => setToastParams(JSON.stringify({ show: false }))} />
            <p>{t("MAINTENANCES.COMPLETED.DESCRIPTION")}</p>
            <div className="filter-maintenance">
                <Filters
                    id={"veiculos"}
                    isMulti={false}
                    title={t("VIEW_MAP_FILTERS.GROUPS")}
                    hasSubTitle={true}
                    subTitle={t("VIEW_MAP_FILTERS.ALL_GROUPS")}
                    options={vehicleGroups || []}
                    icon={HiOutlineRectangleGroup}
                    hasIcon={true}
                    pageTitle={"maintenances_scheduling"}
                />
                <Filters
                    id={"veiculosGroups"}
                    isMulti={false}
                    title={t("VIEW_MAP_FILTERS.VEHICLES")}
                    hasSubTitle={true}
                    subTitle={t("VIEW_MAP_FILTERS.ALL_VEHICLES")}
                    options={vehicles || []}
                    icon={FaCar}
                    hasIcon={true}
                    pageTitle={"maintenances_scheduling"}
                    isLoading={isLoadingVehicles}
                />
                <div className="field">
                    <div className="field-data">
                        <SyncFusionDateTimePicker
                            id="datetimepickerStart"
                            value={startDate}
                            onChange={setStartDate}
                            placeholder={t("SEARCH.INITIAL_DATE_TIME")}
                            step={30}
                        />
                    </div>
                    <span>{t("MAINTENANCES.COMPLETED.UNTIL")}</span>
                    <div className="field-data">
                        <SyncFusionDateTimePicker
                            id="datetimepickerEnd"
                            value={endDate}
                            onChange={setEndDate}
                            placeholder={t("SEARCH.INITIAL_DATE_TIME")}
                            step={30}
                        />
                    </div>
                </div>
                <button className="btn btn-primary" onClick={handleSearch} disabled={isLoading}>
                    {t("MAINTENANCES.COMPLETED.SEARCH")}
                </button>
            </div>

            <div className="buttons-pending">
                <button className="btn btn-primary" onClick={handleDownloadXls} disabled={isLoading}>
                    <TbFileTypeXls />
                    Excel
                </button>
                <button className="btn btn-secondary" onClick={handleDownloadPdf} disabled={isLoading}>
                    <TbFileTypePdf />
                    PDF
                </button>
            </div>

            {isLoading && <Spinner />}

            {tableData.length > 0 && (
                <div className="card mt-3">
                    <GridComponent dataSource={tableData} allowPaging rowHeight={30} pageSettings={{ pageSize }} height="auto">
                        <ColumnsDirective>
                            <ColumnDirective field="maintenanceItem" headerText="Item" width="150" />
                            <ColumnDirective field="execution" headerText="Data" width="120"
                                template={(props) => {
                                    const d = new Date(props.execution);
                                    if (!props.execution || d.getFullYear() <= 1) return null;
                                    return <span>{d.toLocaleDateString('pt-BR')}</span>;
                                }}
                            />
                            <ColumnDirective field="currentOdometer" headerText="KM Atual" width="100" textAlign="Right" />
                            <ColumnDirective field="days" headerText={t("MAINTENANCES.SCHEDULING.DAYS")} width="80" textAlign="Right" />
                            <ColumnDirective field="maintenanceStatus" headerText="Status" width="80" textAlign="Center" />
                        </ColumnsDirective>
                        <Inject services={[Page]} />
                    </GridComponent>
                </div>
            )}
        </div>
    );
}
